import React, { useState } from 'react';

import Card from '@/components/Card';
import Page from '@/components/Page';
import Stack from '@/components/Stack';
import TextInput from '@/components/TextInput';
import { Button } from '@/components/Button';
import { Body } from '@/components/Typography';
import { axiosRubyClient } from '@/util/axios/axiosClient';

const Compliance: React.FC = () => {
  const [workerId, setWorkerId] = useState('');
  const [removeServiceId, setRemoveServiceId] = useState('');
  const [adminKey, setAdminKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!workerId.trim() || !removeServiceId.trim() || !adminKey.trim()) {
      setMessage({
        type: 'error',
        text: 'All fields are required.',
      });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const response = await axiosRubyClient.post('/api/compliances', {
        worker_id: workerId,
        remote_service_id: removeServiceId,
        admin_key: adminKey,
      });

      setMessage({
        type: 'success',
        text: response.data.message,
      });

      // Reset form
      setWorkerId('');
      setRemoveServiceId('');
      setAdminKey('');
    } catch (error: any) {
      console.error(
        'Error creating compliance record:',
        error.response?.data?.error,
      );
      setMessage({
        type: 'error',
        text:
          error.response?.data?.errors ||
          'Error creating compliance record. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page size="full" title="Compliance">
      <Card>
        <Card.Section>
          <form onSubmit={handleSubmit}>
            <Stack gap={24} vertical>
              <div>
                <label
                  htmlFor="worker-id"
                  style={{
                    display: 'block',
                    marginBottom: '6px',
                    color: '#6B7280',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                >
                  Worker ID
                </label>
                <TextInput
                  id="worker-id"
                  placeholder="Enter worker ID"
                  value={workerId}
                  onChange={(e) => setWorkerId(e.currentTarget.value)}
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="remove-service-id"
                  style={{
                    display: 'block',
                    marginBottom: '6px',
                    color: '#6B7280',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                >
                  Remote Service ID
                </label>
                <TextInput
                  id="remove-service-id"
                  placeholder="Enter remove service ID"
                  value={removeServiceId}
                  onChange={(e) => setRemoveServiceId(e.currentTarget.value)}
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="admin-key"
                  style={{
                    display: 'block',
                    marginBottom: '6px',
                    color: '#6B7280',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                >
                  Admin Key
                </label>
                <TextInput
                  id="admin-key"
                  placeholder="Enter admin key"
                  value={adminKey}
                  onChange={(e) => setAdminKey(e.currentTarget.value)}
                  required
                />
              </div>

              {message && (
                <div
                  style={{
                    padding: '12px',
                    borderRadius: '4px',
                    backgroundColor:
                      message.type === 'success' ? '#d4edda' : '#f8d7da',
                    border: `1px solid ${message.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
                    color: message.type === 'success' ? '#155724' : '#721c24',
                  }}
                >
                  <Body>{message.text}</Body>
                </div>
              )}

              <div>
                <Button
                  a11yLabel="Submit compliance form"
                  type="submit"
                  isLoading={loading}
                  disabled={loading}
                  appearance="primary"
                  label={loading ? 'Submitting...' : 'Submit'}
                />
              </div>
            </Stack>
          </form>
        </Card.Section>
      </Card>
    </Page>
  );
};

export default Compliance;
